import { AxiosError } from 'axios';
import { ApiError } from '../types/api';
import { isAuthSessionExpiredHandledError } from './authSession';
import { toastManager } from './notifications';

export type FieldErrorMap = Record<string, string>;

export type BillingErrorEnvelope = {
  error: {
    code: string;
    message: string;
    trace_id?: string;
    details?: Record<string, unknown>;
  };
};

/** Parse unified billing `{ error: { code, message, trace_id, details } }` from an Axios error. */
export function parseBillingErrorEnvelope(error: unknown): BillingErrorEnvelope['error'] | null {
  if (!(error instanceof AxiosError)) return null;
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return null;

  const envelope = (data as BillingErrorEnvelope).error;
  if (envelope && typeof envelope.code === 'string' && typeof envelope.message === 'string') {
    return {
      code: envelope.code,
      message: envelope.message,
      trace_id: envelope.trace_id,
      details: envelope.details ?? {},
    };
  }

  // Legacy FastAPI `detail` (string or validation list)
  const legacy = data as { detail?: string | { msg?: string; message?: string }[] };
  if (typeof legacy.detail === 'string' && legacy.detail) {
    return { code: 'HTTP_ERROR', message: legacy.detail, details: {} };
  }
  if (Array.isArray(legacy.detail) && legacy.detail.length > 0) {
    const first = legacy.detail[0];
    const msg = first?.msg || first?.message || 'Validation error';
    return {
      code: 'VALIDATION_ERROR',
      message: msg,
      details: { fields: legacy.detail },
    };
  }

  // Older services: flat message / error_code
  const flat = data as { message?: string; error_code?: string };
  if (typeof flat.message === 'string' && flat.message) {
    return {
      code: flat.error_code || 'HTTP_ERROR',
      message: flat.message,
      details: {},
    };
  }

  return null;
}

/** True for HTTP 422 / structured validation envelopes — should be shown inline, not as toast. */
export function isApiValidationError(error: unknown): boolean {
  if (error instanceof AxiosError && error.response?.status === 422) {
    return true;
  }
  const billing = parseBillingErrorEnvelope(error);
  return billing?.code === 'VALIDATION_ERROR';
}

/** Map API validation payloads to field-level messages when loc/name is available. */
export function parseApiFieldErrors(error: unknown): FieldErrorMap {
  if (!(error instanceof AxiosError)) {
    return {};
  }
  const data = error.response?.data;
  if (!data || typeof data !== 'object') {
    return {};
  }

  const billing = parseBillingErrorEnvelope(error);
  const billingFields = billing?.details?.fields;
  if (Array.isArray(billingFields)) {
    return billingFields.reduce<FieldErrorMap>((acc, item) => {
      const row = item as { loc?: unknown[]; msg?: string; message?: string };
      const loc = Array.isArray(row.loc) ? row.loc : [];
      const key = String(loc[loc.length - 1] ?? '');
      const msg = row.msg || row.message;
      if (key && msg) {
        acc[key] = msg;
      }
      return acc;
    }, {});
  }

  const legacy = data as {
    detail?: string | { loc?: unknown[]; msg?: string; message?: string }[];
  };
  if (Array.isArray(legacy.detail)) {
    return legacy.detail.reduce<FieldErrorMap>((acc, item) => {
      if (typeof item === 'string') {
        return acc;
      }
      const loc = Array.isArray(item.loc) ? item.loc : [];
      const key = String(loc[loc.length - 1] ?? '');
      const msg = item.msg || item.message;
      if (key && msg) {
        acc[key] = msg;
      }
      return acc;
    }, {});
  }

  return {};
}

export type ShowErrorToastOptions = {
  /** When false (default), 422 validation responses are not toasted — use inline field errors. */
  allowValidationToast?: boolean;
};

export const handleApiError = (error: unknown): string => {
  if (isAuthSessionExpiredHandledError(error)) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof DOMException) {
    if (error.name === 'TimeoutError') {
      return error.message || 'Request timed out.';
    }
    if (error.name === 'AbortError') {
      const cause = (error as DOMException & { cause?: unknown }).cause;
      if (cause instanceof Error && cause.message) {
        return cause.message;
      }
      if (typeof error.message === 'string' && error.message.includes('aborted without reason')) {
        return 'Request timed out or was cancelled before the server responded. Check gateway (:8080) and AI tutor service, or try again.';
      }
      return error.message || 'Request was cancelled.';
    }
    return error.message || 'Request failed.';
  }
  if (error instanceof AxiosError) {
    const billing = parseBillingErrorEnvelope(error);
    const data = error.response?.data as
      | { error?: ApiError & { code?: string }; error_code?: string; message?: string }
      | undefined;
    const apiError = data?.error ?? data;
    const code = billing?.code ?? apiError?.error_code ?? (apiError as { code?: string })?.code ?? data?.error_code;
    const message = billing?.message ?? apiError?.message ?? data?.message;

    if (error.response?.status === 409) {
      if (typeof message === 'string' && message) {
        return message;
      }
      if (code === 'PLAN_KEY_ALREADY_EXISTS') {
        return 'Plan key already exists.';
      }
    }

    if (code === 'TEACHER_NOT_ACTIVE' || (typeof message === 'string' && message.toUpperCase().includes('TEACHER_NOT_ACTIVE'))) {
      return 'Teacher is not active. Please invite and ensure they accept.';
    }
    if (code === 'LIMIT_EXCEEDED' || (typeof message === 'string' && message.toLowerCase().includes('limit reached'))) {
      return 'Limit reached: max primary/secondary classes per teacher.';
    }
    if (error.response?.status === 409 && typeof message === 'string') {
      const m = message.toLowerCase();
      if (m.includes('assign') || m.includes('assigned') || m.includes('delete')) {
        return 'Cannot delete: teacher assigned to a class.';
      }
    }

    if (message && typeof message === 'string') {
      return message;
    }

    if (error.response?.status === 401) {
      return 'Authentication required. Please login again.';
    }

    if (error.response?.status === 403) {
      return 'You do not have permission to perform this action.';
    }

    if (error.response?.status === 404) {
      return 'The requested resource was not found.';
    }

    if (error.response?.status === 422) {
      const fields = billing?.details?.fields;
      if (Array.isArray(fields) && fields.length > 0) {
        const first = fields[0] as { msg?: string; message?: string };
        return first.msg || first.message || 'Validation error. Please check your input.';
      }
      return 'Validation error. Please check your input.';
    }

    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later.';
    }

    const isTimeout =
      error.code === 'ECONNABORTED' ||
      (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout'));
    if (isTimeout) {
      return (
        'Login/API timed out waiting for the server. The browser did send the request. ' +
        'Check that the gateway is running (e.g. localhost:8080), that gateway can reach platform-service ' +
        '(PLATFORM_SERVICE_URL), and MongoDB is up — then watch the gateway terminal for POST /auth/login.'
      );
    }

    if (error.message === 'Network Error') {
      return 'Network error. Please check your connection.';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};

export const showErrorToast = (error: unknown, options?: ShowErrorToastOptions) => {
  if (options?.allowValidationToast !== true && isApiValidationError(error)) {
    return;
  }
  const message = handleApiError(error);
  if (!message) {
    return;
  }
  toastManager.error(message);
};

/** Toast for unexpected server/network/auth failures — never for validation (422). */
export const showServerErrorToast = (error: unknown) => {
  showErrorToast(error, { allowValidationToast: false });
};

export const showSuccessToast = (message: string) => {
  toastManager.success(message);
};

/** Show a success message in the center of the page (e.g. course/writing feedback). */
export const showSuccessToastCenter = (message: string, duration = 2000) => {
  toastManager.successCenter(message, duration);
};

export const showWarningToast = (message: string) => {
  toastManager.warning(message);
};
